import { useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { queryClient } from 'src/config/query';
import { IUser } from 'src/interfaces/models';
import { Opportunity } from 'src/modules/attendance/@types/models';
import { ATTENDANCE_ADMIN_QUERY_KEYS } from 'src/modules/attendance/constants/query';
import apiAxios from 'src/services/api/axios';
import {
  IApiUseQueryData,
  IApiUseQueryFnData,
  IResponseAPIFilter,
} from 'src/services/api/interfaces';

async function getOpportunityAttendants(
  funnelId: string | number,
  opportunityId: string | number,
  config?: AxiosRequestConfig,
) {
  const response = await apiAxios.get<IResponseAPIFilter<IUser>>(
    `/attendance/admin/funnels/${funnelId}/opportunities/${opportunityId}/attendants`,
    config,
  );

  return response;
}

function useOpportunityAttendantsQuery(
  funnelId: string | number,
  opportunityId: string | number,
  data?: IApiUseQueryData<IUser>,
) {
  return useQuery<IApiUseQueryFnData<IUser>>(
    [
      ATTENDANCE_ADMIN_QUERY_KEYS.FUNNELS_OPPORTUNITIES_ATTENDANTS,
      funnelId,
      opportunityId,
      data?.requestConfig?.params,
    ],
    () =>
      getOpportunityAttendants(funnelId, opportunityId, data?.requestConfig),
    data?.queryOptions,
  );
}

function invalidateOpportunityAttendantsQueries() {
  queryClient.invalidateQueries([
    ATTENDANCE_ADMIN_QUERY_KEYS.FUNNELS_OPPORTUNITIES_ATTENDANTS,
  ]);
}

async function updateOpportunityAttendants(
  funnelId: string | number,
  opportunityId: string | number,
  data: object | FormData,
  config?: AxiosRequestConfig,
) {
  const response = await apiAxios.post<Opportunity>(
    `/attendance/admin/funnels/${funnelId}/opportunities/${opportunityId}/attendants`,
    data,
    config,
  );

  invalidateOpportunityAttendantsQueries();

  return response;
}

async function opportunityAttendantExit(
  funnelId: string | number,
  opportunityId: string | number,
  data: object | FormData = {},
  config?: AxiosRequestConfig,
) {
  const response = await apiAxios.post(
    `/attendance/admin/funnels/${funnelId}/opportunities/${opportunityId}/attendant-exit`,
    data,
    config,
  );

  invalidateOpportunityAttendantsQueries();

  return response;
}

export const attendants = {
  getOpportunityAttendants,
  useOpportunityAttendantsQuery,
  updateOpportunityAttendants,
  opportunityAttendantExit,
};
